import React, { useMemo, useState } from "react";
import config from "../config.js";
import {
  fetchConsumableLabels, labelRequestProblem, MAX_LABEL_COPIES,
} from "../utils/consumableLabels";

/**
 * Print labels for several consumables at once (2026-10-05).
 *
 * One 4x6 label per item: the main label on top and a 3" x 3/4" insert along
 * the bottom for the Uline bin label holders, cut out on the dotted lines.
 * `items` is the Consumables sidebar list as currently filtered, so picking a
 * category there and opening this labels that shelf; `initialSelectedIds`
 * pre-ticks the item open on the page.
 */
export default function ConsumableLabelsModal({ items, initialSelectedIds = [], onClose }) {
  const API_BASE = config.API_BASE_URL;
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set(initialSelectedIds));
  const [copies, setCopies] = useState("1");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const shown = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return items;
    return items.filter((c) =>
      (c.product_id || "").toLowerCase().includes(t) ||
      (c.description || "").toLowerCase().includes(t) ||
      (c.category || "").toLowerCase().includes(t));
  }, [items, search]);

  // Print in the order the list shows them, not the order they were ticked.
  const orderedIds = useMemo(
    () => items.map((c) => c.consumable_id).filter((id) => selected.has(id)),
    [items, selected]);
  const copiesNum = Number(copies);
  const problem = labelRequestProblem(orderedIds, copiesNum);
  const labelCount = orderedIds.length * (Number.isInteger(copiesNum) ? copiesNum : 0);

  const toggle = (id, on) => setSelected((prev) => {
    const next = new Set(prev);
    if (on) next.add(id); else next.delete(id);
    return next;
  });
  const selectShown = () => setSelected((prev) => new Set([...prev, ...shown.map((c) => c.consumable_id)]));
  const clearAll = () => setSelected(new Set());

  async function go(mode) {
    setError("");
    // Open the tab now, inside the click: after the await the browser may block it.
    const win = mode === "open" ? window.open("", "_blank") : null;
    if (win) {
      win.opener = null;
      try { win.document.title = "Making labels..."; } catch { /* cross-origin about:blank */ }
    }
    setBusy(true);
    try {
      await fetchConsumableLabels(API_BASE, orderedIds, copiesNum, mode, win);
    } catch (err) {
      setError(err.message || "Could not make the labels.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,.5)", zIndex: 1055 }}
         role="dialog" aria-label="Print bin labels">
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Print bin labels</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <p className="small text-muted mb-2">
              One 4x6 label per item. The bottom of each label is a 3&quot; &times; &frac34;&quot; insert for
              the bin label holders: cut it out on the dotted lines. Print on the 4x6 label printer at
              100% / Actual Size.
            </p>
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <div className="d-flex gap-2 align-items-center mb-2">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by Product ID, Description, Category..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button type="button" className="btn btn-sm btn-outline-secondary text-nowrap"
                      onClick={selectShown} disabled={!shown.length}>
                Select shown ({shown.length})
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearAll}
                      disabled={!selected.size}>
                Clear
              </button>
            </div>
            <ul className="list-group" style={{ maxHeight: "45vh", overflowY: "auto" }}>
              {shown.map((c) => (
                <li key={c.consumable_id} className="list-group-item py-1">
                  <label className="d-flex gap-2 align-items-start mb-0" style={{ cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      className="form-check-input mt-1"
                      checked={selected.has(c.consumable_id)}
                      onChange={(e) => toggle(c.consumable_id, e.target.checked)}
                    />
                    <span>
                      <span className="fw-semibold">{c.product_id || "???"}</span>
                      <span className="text-muted ms-2 small">{c.description}</span>
                      {c.category && <span className="badge bg-light text-dark border ms-2">{c.category}</span>}
                    </span>
                  </label>
                </li>
              ))}
              {!shown.length && <li className="list-group-item text-muted small">Nothing matches.</li>}
            </ul>
          </div>
          <div className="modal-footer justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <label className="small mb-0" htmlFor="consumable-label-copies">Copies of each</label>
              <input
                id="consumable-label-copies"
                type="number"
                min="1"
                max={MAX_LABEL_COPIES}
                className="form-control form-control-sm"
                style={{ width: "5rem" }}
                value={copies}
                onChange={(e) => setCopies(e.target.value)}
              />
              <span className="small text-muted">
                {orderedIds.length} item{orderedIds.length === 1 ? "" : "s"}
                {labelCount ? `, ${labelCount} label${labelCount === 1 ? "" : "s"}` : ""}
              </span>
            </div>
            <div className="d-flex gap-2">
              {problem && orderedIds.length > 0 && <span className="small text-danger align-self-center">{problem}</span>}
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => go("open")}
                      disabled={busy || !!problem}>
                Open PDF
              </button>
              <button type="button" className="btn btn-sm btn-primary" onClick={() => go("print")}
                      disabled={busy || !!problem}>
                {busy ? "Making labels..." : "Print"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
